import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Form,
  HeaderItalic,
  Label,
  Link,
  Separator,
  Text,
} from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { useAuth } from '@/hooks/useAuth';
import { analyticsClient } from '@/lib';
import { userService } from '@/services';

const PLEDGE_ITEMS = ['act', 'byUs', 'zeroTolerance'] as const;

const PledgeHeader = ({ eyebrow, title }: { eyebrow: string; title: string }) => (
  <Box>
    <Text
      size="1"
      style={{
        fontFamily: 'var(--code-font-family)',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--accent-9)',
      }}
    >
      {eyebrow}
    </Text>
    <HeaderItalic mt="2" style={{ whiteSpace: 'pre-line' }}>
      {title}
    </HeaderItalic>
  </Box>
);

const PledgeItem = ({
  index,
  isAffirmed,
  title,
  body,
  statusLabel,
  onToggle,
  showSeparator,
}: {
  index: number;
  isAffirmed: boolean;
  title: string;
  body: string;
  statusLabel: string;
  onToggle: () => void;
  showSeparator: boolean;
}) => (
  <Box>
    <Flex gap="3" align="center" className="py-3.5">
      <Box flexGrow="1" className="min-w-0 cursor-pointer" onClick={onToggle}>
        <Flex align="baseline" gap="2" mb="2">
          <Text
            size="3"
            style={{
              fontFamily: 'var(--code-font-family)',
              color: 'var(--accent-9)',
              lineHeight: 1,
            }}
          >
            {String(index + 1).padStart(2, '0')}
          </Text>
          <Text size="3" weight="bold" style={{ color: 'var(--accent-9)', lineHeight: 1.2 }}>
            {title}
          </Text>
        </Flex>
        <Text size="2" color="gray" as="p" style={{ lineHeight: 1.55 }}>
          {body}
        </Text>
        <Text
          size="1"
          mt="2"
          style={{
            color: isAffirmed ? 'var(--accent-9)' : 'rgb(255 255 255 / 0.45)',
            letterSpacing: '0.02em',
          }}
        >
          {statusLabel}
        </Text>
      </Box>
      <Checkbox checked={isAffirmed} onCheckedChange={() => onToggle()} size="3" />
    </Flex>
    {showSeparator ? <Separator size="4" my="4" /> : null}
  </Box>
);

/** v90 s-pulp-pledge — THE PULP PLEDGE, accepted before "You're in". */
export function PulpPledgePage() {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [affirmed, setAffirmed] = useState<Record<string, boolean>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  const allAffirmed = PLEDGE_ITEMS.every(id => affirmed[id]);
  const continueDisabled = !allAffirmed || !termsAccepted;

  const toggleAffirm = (id: string) => {
    setAffirmed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleContinue = async () => {
    if (user) {
      await userService.updateProfile(user.uid, { termsAcceptedAt: new Date().toISOString() });
    }
    analyticsClient.capture('pledge_accepted');
    navigate('/youre-in');
  };

  return (
    <WizardScreen header={<WizardHeader />}>
      <Form.Root>
        <Flex direction="column" gap="4" py="3" pb="5">
          <PledgeHeader eyebrow={t('pledge.eyebrow')} title={t('pledge.title')} />

          {PLEDGE_ITEMS.map((id, index) => {
            const isAffirmed = Boolean(affirmed[id]);

            return (
              <PledgeItem
                key={id}
                index={index}
                isAffirmed={isAffirmed}
                title={t(`pledge.items.${id}.title`)}
                body={t(`pledge.items.${id}.body`)}
                statusLabel={isAffirmed ? t('pledge.affirmed') : t('pledge.tapToAffirm')}
                onToggle={() => toggleAffirm(id)}
                showSeparator={index < PLEDGE_ITEMS.length - 1}
              />
            );
          })}

          <Form.Field name="terms">
            <Flex gap="3" align="center" py="2">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={checked => setTermsAccepted(checked === true)}
                size="3"
              />
              <Label htmlFor="terms" size="2">
                {t('pledge.pledgePrefix')}{' '}
                <Link href="/terms" target="_blank" rel="noopener noreferrer">
                  {tCommon('legal.termsOfUse')}
                </Link>{' '}
                {tCommon('legal.and')}{' '}
                <Link href="/privacy" target="_blank" rel="noopener noreferrer">
                  {tCommon('legal.privacy')}
                </Link>
                .
              </Label>
            </Flex>
          </Form.Field>

          <Button
            type="button"
            onClick={() => void handleContinue()}
            disabled={continueDisabled}
            color="gray"
            highContrast={!continueDisabled}
            fullWidth
            size="lg"
          >
            {t('pledge.continue')}
          </Button>
        </Flex>
      </Form.Root>
    </WizardScreen>
  );
}
